import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";
import {
  BulkUploadWorkspaceFilesBodySchema,
  BulkUploadWorkspaceFilesResponseSchema,
  CreateWorkspaceBodySchema,
  CreateWorkspaceFolderBodySchema,
  DeleteWorkspaceFileParamsSchema,
  DeleteWorkspaceFolderParamsSchema,
  DeleteWorkspaceGitReferenceParamsSchema,
  GetWorkspaceFileParamsSchema,
  GetWorkspaceFileResponseSchema,
  GetWorkspaceGitReferenceParamsSchema,
  GetWorkspaceGitReferenceResponseSchema,
  ListWorkspaceFolderParamsSchema,
  ListWorkspacesParamsSchema,
  ListWorkspacesResponseSchema,
  RenameWorkspaceBodySchema,
  RenameWorkspaceFileBodySchema,
  SetWorkspaceGitReferenceBodySchema,
  UploadWorkspaceFileBodySchema,
  WorkspaceFileEntrySchema,
  WorkspaceFilePreviewResponseSchema,
  WorkspaceFolderEntrySchema,
  WorkspaceGitReferenceEntrySchema,
  WorkspaceIdentityParamsSchema,
  WorkspaceItemSchema,
  WorkspaceListingSchema,
  WorkspaceSlugPathVarsSchema
} from "./schemas";

/** Minimal "I did the action" envelope for endpoints that have no real
 *  payload to return. Every endpoint declares a Response so the codegen
 *  produces a consistent type-safe client; callers just see `{ deleted: true }`
 *  etc. as confirmation rather than a 204. */
const DeletedAckSchema = z.object({ deleted: z.literal(true) });
const RenamedAckSchema = z.object({ renamed: z.literal(true) });

/**
 * Workspaces API contract — single source of truth for every
 * `/api/workspaces` endpoint. Backend `safeParse`s requests against these;
 * frontend infers its API client types from them.
 */
export const ZWorkspaces = {
  // -------------------------------------------------------------------------
  // Workspace lifecycle
  // -------------------------------------------------------------------------

  List: {
    method: "GET" as const,
    path: "/api/workspaces",
    Params: ListWorkspacesParamsSchema,
    Response: ApiSuccessSchema(ListWorkspacesResponseSchema)
  },

  Create: {
    method: "POST" as const,
    path: "/api/workspaces",
    Body: CreateWorkspaceBodySchema,
    // A freshly created workspace has no files — no point fabricating empty
    // stats. Use the bare item shape; the next List call will return stats.
    Response: ApiSuccessSchema(WorkspaceItemSchema)
  },

  Rename: {
    method: "PATCH" as const,
    path: "/api/workspaces/:slug",
    PathVars: WorkspaceSlugPathVarsSchema,
    Body: RenameWorkspaceBodySchema,
    Response: ApiSuccessSchema(WorkspaceItemSchema)
  },

  Delete: {
    method: "DELETE" as const,
    path: "/api/workspaces/:slug",
    PathVars: WorkspaceSlugPathVarsSchema,
    Response: ApiSuccessSchema(DeletedAckSchema)
  },

  // -------------------------------------------------------------------------
  // File operations within a workspace
  // -------------------------------------------------------------------------

  ListFolder: {
    method: "GET" as const,
    path: "/api/workspaces/:slug/files",
    PathVars: WorkspaceSlugPathVarsSchema,
    Params: ListWorkspaceFolderParamsSchema,
    Response: ApiSuccessSchema(WorkspaceListingSchema)
  },

  GetFile: {
    method: "GET" as const,
    path: "/api/workspaces/:slug/file",
    PathVars: WorkspaceSlugPathVarsSchema,
    Params: GetWorkspaceFileParamsSchema,
    Response: ApiSuccessSchema(GetWorkspaceFileResponseSchema)
  },

  /**
   * Server-side search over the workspace's text docs by keyword and/or
   * frontmatter (NEX-2407). One call: no mount, no recursive client glob.
   * Returns matching paths with snippets + parsed frontmatter. At least one of
   * `query` / `frontmatter` is required.
   */
  /**
   * Download a folder (and its whole subtree) as a streamed ZIP. Returns a
   * binary blob (`application/zip`), not JSON — `responseType: "blob"` makes the
   * codegen emit a Blob-returning mutation hook. `path` selects the folder
   * (empty = workspace root).
   */
  DownloadFolderArchive: {
    method: "GET" as const,
    path: "/api/workspaces/:slug/folder-archive",
    PathVars: WorkspaceSlugPathVarsSchema,
    Params: ListWorkspaceFolderParamsSchema,
    responseType: "blob" as const
  },

  /**
   * In-UI preview for a single file. The file's extension picks the strategy
   * (shared `workspacePreviewFor` map): `url` → an inline presigned URL the
   * browser renders natively; `content` → UTF-8 text read + capped server-side.
   */
  Preview: {
    method: "GET" as const,
    path: "/api/workspaces/:slug/file/preview",
    PathVars: WorkspaceSlugPathVarsSchema,
    Params: GetWorkspaceFileParamsSchema,
    Response: ApiSuccessSchema(WorkspaceFilePreviewResponseSchema)
  },

  UploadFile: {
    method: "POST" as const,
    path: "/api/workspaces/:slug/upload",
    PathVars: WorkspaceSlugPathVarsSchema,
    Params: WorkspaceIdentityParamsSchema,
    Body: UploadWorkspaceFileBodySchema,
    Response: ApiSuccessSchema(WorkspaceFileEntrySchema)
  },

  /**
   * Bulk upload: N files in one multipart request, server-side parallel
   * SOURCE+MIRROR writes via Promise.allSettled. Per-file status returned.
   * Currently unused by the frontend (folder upload was removed) — kept
   * ready for tarball/folder pipeline work.
   */
  BulkUploadFiles: {
    method: "POST" as const,
    path: "/api/workspaces/:slug/upload-batch",
    PathVars: WorkspaceSlugPathVarsSchema,
    Params: WorkspaceIdentityParamsSchema,
    Body: BulkUploadWorkspaceFilesBodySchema,
    Response: ApiSuccessSchema(BulkUploadWorkspaceFilesResponseSchema)
  },

  DeleteFile: {
    method: "DELETE" as const,
    path: "/api/workspaces/:slug/file",
    PathVars: WorkspaceSlugPathVarsSchema,
    Params: DeleteWorkspaceFileParamsSchema,
    Response: ApiSuccessSchema(DeletedAckSchema)
  },

  RenameFile: {
    method: "POST" as const,
    path: "/api/workspaces/:slug/rename",
    PathVars: WorkspaceSlugPathVarsSchema,
    Body: RenameWorkspaceFileBodySchema,
    Response: ApiSuccessSchema(RenamedAckSchema)
  },

  CreateFolder: {
    method: "POST" as const,
    path: "/api/workspaces/:slug/folder",
    PathVars: WorkspaceSlugPathVarsSchema,
    Body: CreateWorkspaceFolderBodySchema,
    Response: ApiSuccessSchema(WorkspaceFolderEntrySchema)
  },

  DeleteFolder: {
    method: "DELETE" as const,
    path: "/api/workspaces/:slug/folder",
    PathVars: WorkspaceSlugPathVarsSchema,
    Params: DeleteWorkspaceFolderParamsSchema,
    Response: ApiSuccessSchema(DeletedAckSchema)
  },

  /**
   * Point a DRIVE workspace at a Vibe git project (Chapter 26). Nothing is
   * copied — the repository stays the source of truth and the reference is a
   * pointer, so the view follows the repo with no sync step to drift.
   */
  SetGitReference: {
    method: "POST" as const,
    path: "/api/workspaces/:slug/git-reference",
    PathVars: WorkspaceSlugPathVarsSchema,
    Body: SetWorkspaceGitReferenceBodySchema,
    Response: ApiSuccessSchema(WorkspaceGitReferenceEntrySchema)
  },

  /**
   * Resolve one reference to the project, ref and path it names. The target is
   * re-resolved against the caller's org on every read — the stored id is a
   * request, never authority.
   */
  GetGitReference: {
    method: "GET" as const,
    path: "/api/workspaces/:slug/git-reference",
    PathVars: WorkspaceSlugPathVarsSchema,
    Params: GetWorkspaceGitReferenceParamsSchema,
    Response: ApiSuccessSchema(GetWorkspaceGitReferenceResponseSchema)
  },

  DeleteGitReference: {
    method: "DELETE" as const,
    path: "/api/workspaces/:slug/git-reference",
    PathVars: WorkspaceSlugPathVarsSchema,
    Params: DeleteWorkspaceGitReferenceParamsSchema,
    Response: ApiSuccessSchema(DeletedAckSchema)
  },

  /**
   * Restore a soft-deleted file or folder from the S3 backup (the MIRROR
   * bucket's version history, retained ~30 days — comfortably past the 72h
   * recovery window). The DELETE endpoints purge immediately; this is the
   * recovery path. `path` may be a single file or a folder prefix.
   */
  // -------------------------------------------------------------------------
  // Admin — shared (cross-org) workspace definitions
  //
  // Platform admins manage the workspace DEFINITIONS (create/rename/delete)
  // under `/api/admin/shared-workspaces`, PBAC-guarded by the
  // `admin:shared-workspaces` resource. The list/item shapes are identical to
  // the org-facing endpoints above (same workspace entity + stats), so these
  // reuse the same response schemas — only the path and access model differ.
  // -------------------------------------------------------------------------

  AdminSharedList: {
    method: "GET" as const,
    path: "/api/admin/shared-workspaces",
    Response: ApiSuccessSchema(ListWorkspacesResponseSchema)
  },

  AdminSharedCreate: {
    method: "POST" as const,
    path: "/api/admin/shared-workspaces",
    Body: CreateWorkspaceBodySchema,
    Response: ApiSuccessSchema(WorkspaceItemSchema)
  },

  AdminSharedRename: {
    method: "PATCH" as const,
    path: "/api/admin/shared-workspaces/:slug",
    PathVars: WorkspaceSlugPathVarsSchema,
    Body: RenameWorkspaceBodySchema,
    Response: ApiSuccessSchema(WorkspaceItemSchema)
  },

  AdminSharedDelete: {
    method: "DELETE" as const,
    path: "/api/admin/shared-workspaces/:slug",
    PathVars: WorkspaceSlugPathVarsSchema,
    Response: ApiSuccessSchema(DeletedAckSchema)
  }
} as const;
